import { Scatter, ScatterChart } from 'recharts';
import useCalendarChart, { CalendarChartProps } from './CalendarChart';

export type SexualActivityChartProps = CalendarChartProps & {
  dayValues: (string | null)[];
};

interface ScatterPoint {
  dayIndex: number;
  dayValue: number;
}

// raw path data for the antd icons, each with its own viewBox
const ICON_SIZE = 14;

const CLOSE_PATH =
  'M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z';
const CARET_DOWN_PATH =
  'M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z';
const CARET_UP_PATH =
  'M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z';

export const SEXUAL_ACTIVITY_ICON_PATHS: Record<string, string> = {
  NONE: CLOSE_PATH,
  UNPROTECTED: CARET_DOWN_PATH,
  PROTECTED: CARET_UP_PATH,
};

const ICON_VIEWBOXES: Record<string, string> = {
  NONE: '64 64 896 896',
  UNPROTECTED: '0 0 1024 1024',
  PROTECTED: '0 0 1024 1024',
};

export const SEXUAL_ACTIVITY_COLORS: Record<string, string> = {
  NONE: 'var(--subtle-foreground)',
  UNPROTECTED: 'var(--chart-3)',
  PROTECTED: 'var(--inverse)',
};

const VALUES = ['NONE', 'PROTECTED', 'UNPROTECTED'] as const;

// renders an antd icon (as raw svg path data) centered on a scatter point, following the same nested-svg
// positioning trick as `caretDown` in CalendarChart.tsx
export const makeIconShape =
  (pathData: string, viewBox: string, color: string) =>
  ({ cx, cy }: { cx?: number; cy?: number }) => {
    if (cx == null || cy == null || isNaN(cx) || isNaN(cy)) return null;
    return (
      <svg
        x={cx - ICON_SIZE / 2}
        y={cy - ICON_SIZE / 2}
        width={ICON_SIZE}
        height={ICON_SIZE}
        viewBox={viewBox}
        style={{ overflow: 'visible' }}
      >
        <path d={pathData} fill={color} />
      </svg>
    );
  };

const ICON_SHAPES = Object.fromEntries(
  VALUES.map(value => [
    value,
    makeIconShape(SEXUAL_ACTIVITY_ICON_PATHS[value], ICON_VIEWBOXES[value], SEXUAL_ACTIVITY_COLORS[value]),
  ]),
);

const SexualActivityChart: React.FC<SexualActivityChartProps> = ({
  dayLabels,
  dayValues,
  dayRange,
  periodDots,
  drawHollowPeriodDots,
  fertilityDots,
  topLabels,
  showMonthLabels,
}) => {
  const { graphHeight, domainMaxDay, domainMinDay, chartBuildingBlocks, yRange } = useCalendarChart({
    dayLabels,
    dayRange,
    periodDots,
    drawHollowPeriodDots,
    fertilityDots,
    topLabels,
    showMonthLabels,
    yRangeOverride: 2,
  });

  const offset = dayLabels[0].diff(domainMinDay, 'days');
  // icons sit centered in the data unit directly above the day-number row (unit [0, 1])
  const iconDayValue = yRange - 0.5;

  const scatterPoints = VALUES.map(value => ({
    value,
    points: dayValues.reduce<ScatterPoint[]>((points, dayValue, dayIndex) => {
      if (dayValue === value) points.push({ dayIndex: dayIndex + 0.5 + offset, dayValue: iconDayValue });
      return points;
    }, []),
  }));

  return (
    <div data-testid="sexual-activity-chart">
      <ScatterChart
        width={(domainMaxDay.diff(domainMinDay, 'days') + 1) * 14}
        height={graphHeight}
        data={dayValues}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      >
        {chartBuildingBlocks}
        {scatterPoints.map(({ value, points }) => (
          <Scatter key={value} name={value} data={points} shape={ICON_SHAPES[value]} animationDuration={100} />
        ))}
      </ScatterChart>
    </div>
  );
};
export default SexualActivityChart;
